import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '../state/atoms';
import { AnalysisCuboid, CuboidClickMode, PointOfInterestWithCuboids } from '@/types';
import * as poiState from '../../pointOfInterest/state';
import { allVoiTemplates as allVoiTemplatesState } from '@/components/Analysis/state';
import { StateMap } from '@/utils/stateMap';
import { PointOfInterest } from '@/__generated__/graphql';
import { useCallback } from 'react';
import { useCuboidState } from './useCuboidState';
import { checkIfVoi } from '@/utils/poi';

export const useAnalysisCuboidActions = () => {
  const cuboidState = useCuboidState();
  const setCuboidClickMode = useSetAtom(cuboidState.cuboidClickMode);
  const [currentCuboid, setCurrentCuboid] = useAtom(cuboidState.currentCuboid);
  const setIsCuboidEditorEnabled = useSetAtom(cuboidState.isCuboidEditorEnabled);
  const setCuboidTransformMode = useSetAtom(cuboidState.cuboidTransformMode);
  const cancelPOIAdd = useSetAtom(poiState.cancelPOIAdd);
  const setReferenceCuboid = useSetAtom(cuboidState.referenceCuboid);

  const setCuboidFlowEnded = useSetAtom(state.isCuboidFlowEnded);
  const setCuboids = useSetAtom(state.cuboids);
  const setEditingCuboids = useSetAtom(state.editingCuboids);
  const setIsAddingWorkscope = useSetAtom(state.isAddingWorkscope);
  const allVoiTemplates = useAtomValue(allVoiTemplatesState);

  const startAddingCuboid = useCallback(() => {
    setCurrentCuboid(undefined);
    setIsCuboidEditorEnabled(true);
    setCuboidFlowEnded(false);
    setCuboidClickMode(CuboidClickMode.AddCuboid);
  }, [setCurrentCuboid, setIsCuboidEditorEnabled, setCuboidClickMode, setCuboidFlowEnded]);

  const stopAddingCuboid = useCallback(() => {
    setCurrentCuboid(undefined);
    setIsCuboidEditorEnabled(false);
    setCuboidFlowEnded(false);
    setCuboidClickMode(CuboidClickMode.Default);
    setEditingCuboids(new StateMap<string, AnalysisCuboid>());
    setIsAddingWorkscope(false);
    setReferenceCuboid(undefined);
  }, [
    setCurrentCuboid,
    setIsCuboidEditorEnabled,
    setCuboidClickMode,
    setEditingCuboids,
    setCuboidFlowEnded,
    setIsAddingWorkscope,
    setReferenceCuboid,
  ]);

  const cancelAddingCuboid = useCallback(() => {
    setCurrentCuboid(undefined);
    setIsCuboidEditorEnabled(false);
    setCuboidFlowEnded(false);
    setEditingCuboids(new StateMap<string, AnalysisCuboid>());
    setReferenceCuboid(undefined);

    // clearing out the poi editing state
    cancelPOIAdd();
  }, [
    setCurrentCuboid,
    setIsCuboidEditorEnabled,
    setEditingCuboids,
    cancelPOIAdd,
    setCuboidFlowEnded,
    setReferenceCuboid,
  ]);

  const initializeCuboids = useCallback(
    (allPois: PointOfInterestWithCuboids[]) => {
      const vois = allPois.filter((poi) => checkIfVoi(allVoiTemplates, poi.type));
      const voidsCuboidsFlatMap = vois
        .flatMap((poi) =>
          poi.cuboids?.map((cuboid) => ({
            ...cuboid,
            poiId: poi.id,
          }))
        )
        .filter((cuboid) => !!cuboid);
      const voiMap = new StateMap<string, AnalysisCuboid>().asMutableStateMap();

      voidsCuboidsFlatMap.forEach((voi) => {
        if (!voi) return;

        voiMap.set(voi.id, {
          id: voi.id,
          position: voi.position as [number, number, number],
          rotation: voi.rotation as [number, number, number],
          scale: voi.scale as [number, number, number],
          poiId: voi?.poiId,
        });
      });

      setCuboids(voiMap.asStateMap());
    },
    [setCuboids, allVoiTemplates]
  );

  const doneAddingCuboid = useCallback(() => {
    setReferenceCuboid(undefined);
    setIsCuboidEditorEnabled(false);
    setTimeout(() => {
      setCuboidFlowEnded(true);
    }, 800);
  }, [setIsCuboidEditorEnabled, setCuboidFlowEnded, setReferenceCuboid]);

  const startEditingPoiCuboids = useCallback(
    (poi: PointOfInterest | undefined) => {
      const poiCuboids = poi?.cuboids;
      if (!poiCuboids) return;

      const poiCuboidIds = poiCuboids.map((cuboid) => cuboid.id);

      setEditingCuboids((current) => {
        const copy = current.asMutableStateMap();
        copy.setMany(
          poiCuboids.map((cuboid) => [
            cuboid.id,
            {
              id: cuboid.id,
              position: cuboid.position as [number, number, number],
              rotation: cuboid.rotation as [number, number, number],
              scale: cuboid.scale as [number, number, number],
            },
          ])
        );

        return copy.asStateMap();
      });

      // remove these ids from cuboids
      setCuboids((current) => {
        const copy = current.asMutableStateMap();
        copy.deleteMany(poiCuboidIds);
        return copy.asStateMap();
      });
    },
    [setCuboids, setEditingCuboids]
  );

  const stopEditingPoiCuboids = useCallback(
    (poi: Partial<PointOfInterest> | undefined) => {
      const poiCuboids = poi?.cuboids;
      if (!poiCuboids) return;

      setCuboids((current) => {
        const copy = current.asMutableStateMap();
        copy.setMany(
          poiCuboids.map((cuboid) => [
            cuboid.id,
            {
              id: cuboid.id,
              position: cuboid.position as [number, number, number],
              rotation: cuboid.rotation as [number, number, number],
              scale: cuboid.scale as [number, number, number],
            },
          ])
        );

        return copy.asStateMap();
      });

      setEditingCuboids(new StateMap<string, AnalysisCuboid>());
      setCurrentCuboid(undefined);
      setIsCuboidEditorEnabled(false);
      setCuboidFlowEnded(false);
    },
    [setCuboids, setEditingCuboids, setCurrentCuboid, setIsCuboidEditorEnabled, setCuboidFlowEnded]
  );

  const doneEditingPoiCuboids = useCallback(() => {
    setIsCuboidEditorEnabled(false);
    setCuboidFlowEnded(false);
    setCurrentCuboid(undefined);
    setEditingCuboids(new StateMap<string, AnalysisCuboid>());
  }, [setIsCuboidEditorEnabled, setCurrentCuboid, setEditingCuboids, setCuboidFlowEnded]);

  const startEditingCurrentCuboid = useCallback(() => {
    setIsCuboidEditorEnabled(true);
    setCuboidFlowEnded(false);
    setCuboidClickMode(CuboidClickMode.SelectCuboid);
  }, [setCuboidClickMode, setCuboidFlowEnded, setIsCuboidEditorEnabled]);

  const getSelectedCuboid = useCallback(() => {
    return currentCuboid;
  }, [currentCuboid]);

  return {
    stopAddingCuboid,
    setCuboidTransformMode,
    cancelAddingCuboid,
    initializeCuboids,
    doneAddingCuboid,
    startEditingPoiCuboids,
    stopEditingPoiCuboids,
    doneEditingPoiCuboids,
    startAddingCuboid,
    getSelectedCuboid,
    startEditingCurrentCuboid,
  };
};
